import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import type { ConflictEventSide } from '../../lib/conflicts'
import { formatConflictTimeRange } from '../../lib/conflicts'

type ConflictTimelineProps = {
  workout: ConflictEventSide
  calendarEvent: ConflictEventSide
  overlapMinutes: number
}

function barMetrics(
  startsAt: string,
  endsAt: string,
  windowStart: number,
  windowSpan: number,
): { left: number; width: number } {
  const start = new Date(startsAt).getTime()
  const end = new Date(endsAt).getTime()
  const left = ((start - windowStart) / windowSpan) * 100
  const width = ((end - start) / windowSpan) * 100
  return {
    left: Math.max(0, Math.min(100, left)),
    width: Math.max(2, Math.min(100 - left, width)),
  }
}

const ConflictTimeline = ({ workout, calendarEvent, overlapMinutes }: ConflictTimelineProps) => {
  const { t } = useTranslation()

  const windowStart = Math.min(
    new Date(workout.startsAt).getTime(),
    new Date(calendarEvent.startsAt).getTime(),
  )
  const windowEnd = Math.max(
    new Date(workout.endsAt).getTime(),
    new Date(calendarEvent.endsAt).getTime(),
  )
  const windowSpan = Math.max(windowEnd - windowStart, 1)

  const overlapStart = Math.max(
    new Date(workout.startsAt).getTime(),
    new Date(calendarEvent.startsAt).getTime(),
  )
  const overlapEnd = Math.min(
    new Date(workout.endsAt).getTime(),
    new Date(calendarEvent.endsAt).getTime(),
  )
  const overlapBar =
    overlapEnd > overlapStart
      ? barMetrics(new Date(overlapStart).toISOString(), new Date(overlapEnd).toISOString(), windowStart, windowSpan)
      : null

  const workoutBar = barMetrics(workout.startsAt, workout.endsAt, windowStart, windowSpan)
  const calendarBar = barMetrics(calendarEvent.startsAt, calendarEvent.endsAt, windowStart, windowSpan)

  const rows: Array<{
    side: ConflictEventSide
    bar: { left: number; width: number }
    icon: string
    accent: string
    track: string
    label: string
  }> = [
    {
      side: workout,
      bar: workoutBar,
      icon: 'exercise',
      accent: 'bg-kilo-accent',
      track: 'bg-kilo-accent/20',
      label: t('calendar.conflictTimeline.kiloSession'),
    },
    {
      side: calendarEvent,
      bar: calendarBar,
      icon: calendarEvent.isWorkEvent ? 'work' : 'event',
      accent: 'bg-amber-400',
      track: 'bg-amber-400/20',
      label: calendarEvent.isWorkEvent
        ? t('calendar.conflictTimeline.workCalendar')
        : t('calendar.conflictTimeline.calendar'),
    },
  ]

  return (
    <div className="mt-4 space-y-3 rounded-2xl border border-amber-900/40 bg-black/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">{t('calendar.conflictTimeline.overlap')}</p>
        {overlapMinutes > 0 ? (
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-300">
            {t('calendar.conflictTimeline.minClash', { minutes: overlapMinutes })}
          </p>
        ) : null}
      </div>

      <div className="relative mb-1 h-2 overflow-hidden rounded-full bg-neutral-900">
        {overlapBar ? (
          <div
            className="absolute inset-y-0 rounded-full bg-red-500/70"
            style={{ left: `${overlapBar.left}%`, width: `${overlapBar.width}%` }}
            aria-hidden
          />
        ) : null}
      </div>

      {rows.map(({ side, bar, icon, accent, track, label }) => (
        <div key={side.kind} className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-neutral-900">
              <MaterialIcon name={icon} filled size="sm" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
              <p className="mt-0.5 truncate font-semibold text-white">{side.title}</p>
              <p className="mt-0.5 text-xs text-neutral-400">{formatConflictTimeRange(side.startsAt, side.endsAt)}</p>
            </div>
          </div>
          <div className={`relative h-2 overflow-hidden rounded-full ${track}`}>
            <div
              className={`absolute inset-y-0 rounded-full ${accent}`}
              style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
              aria-hidden
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ConflictTimeline
